<template>
    <div class="cms-container">
        <div v-if="showSystemBackupModal" @click="closeModal"
            class="modal-overlay d-flex align-items-center justify-content-center fixed-top w-100 h-100 left-0">
            <ToastCMS ref="toast" />
            <div class="modal-content bg-white overflow-hidden" @click.stop>
                <div class="modal-header d-flex justify-content-between align-items-center px-3 py-3">
                    <h2 class="m-0 text-dark">System Backup</h2>
                    <button class="text-dark bg-transparent border-0 material-symbols-outlined"
                        @click="closeModal">close</button>
                </div>
                <p class=" text-dark my-2 px-3 py-2" style="font-size: 0.9rem;">
                    Since the server does not store permanent files, use this area to save your progress locally.
                </p>
                <div class="w-100 px-3 py-2">
                    <div class="p-3 border rounded bg-light">
                        <div class="text-dark font-weight-bold">Export Data</div>
                        <p class="small text-muted">Download all CMS categories, news, and settings into a single backup
                            file.</p>
                        <button @click="exportData" style="gap:5px;"
                            class="vmc-bg-prime-4 text-white border-0 py-2 px-4 rounded w-100 d-flex align-items-center justify-content-center">
                            <i class="material-symbols-outlined text-white">download</i>
                            Download Backup (.json)
                        </button>
                    </div>
                </div>
                <div class="w-100 px-3 py-2">
                    <div class="p-3 border rounded bg-light">
                        <div class="text-dark font-weight-bold">Restore Data</div>
                        <p class="small text-muted">Upload a previously saved backup file to restore the website
                            content.</p>

                        <input type="file" ref="fileInput" accept=".json" hidden @change="handleImport">

                        <button @click="$refs.fileInput.click()" style="gap:5px;"
                            class="vmc-bg-prime-5 text-white border-0 py-2 px-4 rounded w-100 d-flex align-items-center justify-content-center">
                            <i class="material-symbols-outlined text-white">upload</i>
                            Upload & Restore
                        </button>
                    </div>
                </div>
                <div class="pb-3 px-3">
                    <div class="mt-4 p-3 bg-warning-subtle border border-warning rounded">
                        <p class="mb-0 text-dark" style="font-size: 0.85rem;">
                            <strong class="text-dark">Note:</strong> If the server restarts or loses power, all data
                            will be wiped. You
                            <strong class="text-dark">must</strong> use the "Upload & Restore" button to re-upload the
                            contents.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showChangePasswordModal"
            class="modal-overlay d-flex align-items-center justify-content-center fixed-top w-100 h-100 left-0"
            @click="closeSettingsModal">
            <div class="modal-content bg-white overflow-hidden" @click.stop>
                <div class="modal-header d-flex justify-content-between align-items-center px-3 py-3">
                    <h2 class="m-0 text-dark">Change Password</h2>
                    <button class="material-symbols-outlined text-dark bg-transparent border-0"
                        @click="closeSettingsModal">close</button>
                </div>
                <form class="modal-body mt-1" @submit.prevent="changePassword">
                    <div class="vmc-input-box w-100 mb-3 position-relative">
                        <span class="icon position-absolute" @click="toggleOldPassword">
                            <i v-if="showOldPassword" class="text-dark bi bi-eye-fill"></i>
                            <i v-else class="text-dark bi bi-eye-slash-fill"></i>
                        </span>
                        <input class="text-dark w-100 bg-transparent border-0"
                            :type="showOldPassword ? 'text' : 'password'" v-model="oldpassword" required>
                        <label class="position-absolute">Old Password</label>
                    </div>
                    <div class="vmc-input-box w-100 my-4 position-relative">
                        <span class="icon position-absolute" @click="toggleNewPassword">
                            <i v-if="showNewPassword" class="text-dark bi bi-eye-fill"></i>
                            <i v-else class="text-dark bi bi-eye-slash-fill"></i>
                        </span>
                        <input class="text-dark w-100 bg-transparent border-0"
                            :type="showNewPassword ? 'text' : 'password'" v-model="newpassword" required>
                        <label class="position-absolute">New Password</label>
                    </div>
                    <div class="vmc-input-box w-100 my-4 position-relative">
                        <span class="icon position-absolute" @click="toggleConfirmNewPassword">
                            <i v-if="showConfirmNewPassword" class="text-dark bi bi-eye-fill"></i>
                            <i v-else class="text-dark bi bi-eye-slash-fill"></i>
                        </span>
                        <input class="text-dark w-100 bg-transparent border-0"
                            :type="showConfirmNewPassword ? 'text' : 'password'" v-model="confirmnewpassword" required>
                        <label class="position-absolute">Confirm New Password</label>
                    </div>

                    <p v-if="changePasswordError" class="text-danger text-center mb-2" style="font-size:0.85rem;">
                        {{ changePasswordError }}
                    </p>

                    <div class="modal-actions d-flex justify-content-end mt-3">
                        <button type="submit" class="vmc-bg-prime-5 px-3 py-1 rounded text-white border-0"
                            style="cursor:pointer;" :disabled="changePasswordLoading">
                            {{ changePasswordLoading ? 'Saving...' : 'Submit' }}
                        </button>
                        <button type="button" class="vmc-bg-prime-4 px-3 py-1 rounded text-white border-0"
                            style="cursor:pointer;" @click="closeSettingsModal">Cancel</button>
                    </div>
                </form>
            </div>
        </div>

        <div v-if="showEditProfileModal"
            class="modal-overlay d-flex align-items-center justify-content-center fixed-top w-100 h-100 left-0"
            @click="closeSettingsModal">
            <div class="modal-content bg-white overflow-hidden" @click.stop>
                <div class="modal-header d-flex justify-content-between align-items-center px-3 py-3">
                    <h2 class="m-0 text-dark">Edit Profile</h2>
                    <button class="material-symbols-outlined text-dark bg-transparent border-0"
                        @click="closeSettingsModal">close</button>
                </div>
                <form class="modal-body" @submit.prevent="saveProfile">
                    <div class="d-flex flex-column align-items-center mb-3">
                        <div class="position-relative">
                            <img :src="profile2" alt="" id="profilePreview" class="rounded-circle"
                                style="width:110px; height:110px; object-fit:cover;">
                            <label for="profileImage"
                                class="select-photo material-symbols-outlined text-white p-2 rounded-circle vmc-bg-prime-5"
                                style="cursor:pointer; transition: 0.3s">
                                photo_camera
                            </label>
                            <input type="file" id="profileImage" accept="image/*" class="d-none"
                                @change="previewImage($event)">
                        </div>
                        <button type="button" class="mt-2 vmc-bg-prime-6 text-white border-0 rounded px-3 py-1"
                            @click="removeProfileImage($event)">
                            Remove Photo
                        </button>
                    </div>
                    <div class="vmc-input-box mt-2 mb-4 position-relative">
                        <span class="icon position-absolute"><i class="text-dark bi bi-person-fill"></i></span>
                        <input class="text-dark w-100 bg-transparent border-0" type="text" v-model="editProfileName"
                            required>
                        <label class="position-absolute">Account Name</label>
                    </div>
                    <div class="vmc-input-box w-100 mb-4 position-relative">
                        <span class="icon position-absolute" @click="togglePassword">
                            <i v-if="showPassword" class="text-dark bi bi-eye-fill"></i>
                            <i v-else class="text-dark bi bi-eye-slash-fill"></i>
                        </span>
                        <input class="text-dark w-100 bg-transparent border-0"
                            :type="showPassword ? 'text' : 'password'" v-model="password" required>
                        <label class="position-absolute">Password</label>
                    </div>

                    <p v-if="editProfileError" class="text-danger text-center mb-2" style="font-size:0.85rem;">
                        {{ editProfileError }}
                    </p>

                    <div class="modal-actions d-flex justify-content-end mt-3">
                        <button type="submit" class="vmc-bg-prime-5 rounded px-3 py-1 text-white border-0"
                            :disabled="editProfileLoading">
                            {{ editProfileLoading ? 'Saving...' : 'Submit' }}
                        </button>
                        <button type="button" class="vmc-bg-prime-4 rounded px-3 py-1 text-white border-0"
                            @click="closeSettingsModal">Cancel</button>
                    </div>
                </form>
            </div>
        </div>

        <div class="header-component">
            <header
                class="vmc-mobile-header fixed-top left-0 right-0 d-flex align-items-center justify-content-between px-3 py-1">
                <button ref="toggleBtn" @click.stop="toggleSidebar($event)"
                    class="toggle-btn border-0 bg-transparent text-white">☰</button>
                <img :src="vmcLogo" alt="" style="width: 50px;">
                <div class="vmc-profile-wrap">
                    <img :src="profile" alt="" class="vmc-profile-picture rounded-circle" @click.stop="toggleDropdown">
                    <div class="vmc-profile">
                        <transition name="dropdown">
                            <ul v-if="isDropdownOpen"
                                class="dropdown-menu w-100 dropdown-menu-end shadow show vmc-teleported-dropdown">
                                <li class="px-3 py-2 d-flex align-items-center">
                                    <img :src="profile2" class="rounded-circle me-2"
                                        style="width:40px; height:40px; object-fit: cover;">
                                    <strong class="pl-2 text-dark">{{ accountName }}</strong>
                                </li>
                                <li>
                                    <hr class="dropdown-divider">
                                </li>
                                <li>
                                    <button
                                        class="dropdown-menu-items bg-transparent border-0 list-unstyled w-100 py-2 d-flex align-items-center justify-content-between px-3"
                                        @click.stop="toggleSettings">
                                        <div class="icon-lists d-flex align-items-center text-dark">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21"
                                                viewBox="0 0 24 24" fill="none" stroke="#212529" stroke-width="2"
                                                stroke-linecap="round" stroke-linejoin="round">
                                                <path
                                                    d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                            <span class="pl-2 text-dark item-dropdown">Settings</span>
                                        </div>
                                        <span class="icon-dropdown material-symbols-outlined text-dark"
                                            :class="{ 'rotate-settings': isSettingsOpen }">keyboard_arrow_right</span>
                                    </button>
                                    <ul v-if="isSettingsOpen" class="list-unstyled pb-2">
                                        <li>
                                            <button
                                                class="open-settings-btn px-4 py-2 w-100 bg-transparent text-dark d-flex align-items-center border-0"
                                                @click.stop="openSettingsModal('password')">
                                                <div class="icon-lists d-flex align-items-center text-dark">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22"
                                                        viewBox="0 0 24 24" fill="none" stroke="#212529"
                                                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                        <path
                                                            d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
                                                        <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
                                                    </svg>
                                                    <span class="pl-2 text-dark item-dropdown">Change Password</span>
                                                </div>
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                class="open-settings-btn px-4 py-2 w-100 bg-transparent text-dark d-flex align-items-center border-0"
                                                @click.stop="openSettingsModal('profile')">
                                                <div class="icon-lists d-flex align-items-center text-dark">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22"
                                                        viewBox="0 0 24 24" fill="none" stroke="#212529"
                                                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                        <path d="M18 20a6 6 0 0 0-12 0" />
                                                        <circle cx="12" cy="10" r="4" />
                                                        <circle cx="12" cy="12" r="10" />
                                                    </svg>
                                                    <span class="pl-2 text-dark item-dropdown">Edit Profile</span>
                                                </div>
                                            </button>
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <button
                                        class="dropdown-menu-items bg-transparent border-0 list-unstyled w-100 py-2 d-flex align-items-center justify-content-between px-3"
                                        style="text-decoration:none;" @click.stop="logout">
                                        <div class="icon-lists text-dark d-flex align-items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21"
                                                viewBox="0 0 24 24" fill="none" stroke="#212529" stroke-width="2"
                                                stroke-linecap="round" stroke-linejoin="round">
                                                <path d="m16 17 5-5-5-5" />
                                                <path d="M21 12H9" />
                                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                            </svg>
                                            <span class="pl-2 text-dark item-dropdown">Logout</span>
                                        </div>
                                    </button>
                                </li>
                            </ul>
                        </transition>
                    </div>
                </div>
            </header>
            <div class="margin"></div>
        </div>

        <transition name="sidebar" @enter="enterSidebar" @leave="leaveSidebar">
            <nav v-show="isOpen" ref="sidebar" class="sidebar text-white fixed-top left-0"
                style="border-right: 1px solid #666;" @click.stop>
                <header class="vmc-logo d-flex justify-content-center p-2 overflow-hidden">
                    <img :src="vmcLogo" alt="" style="width: 70px;">
                    <button type="button" @click.stop="toggleSidebar" class="sidebar-close-btn bg-transparent border-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                            stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </header>
                <div class="vmc-sidebar-lists">
                    <ul class="m-0 p-0">
                        <li @click.stop>
                            <router-link to="/admin/dashboard"
                                :class="['vmc-lists w-100 d-flex align-items-center px-2 py-3 border-0 text-decoration-none', { 'active-link': isActive('/admin/dashboard') }]">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <rect width="7" height="9" x="3" y="3" rx="1" />
                                    <rect width="7" height="5" x="14" y="3" rx="1" />
                                    <rect width="7" height="9" x="14" y="12" rx="1" />
                                    <rect width="7" height="5" x="3" y="16" rx="1" />
                                </svg>
                                Dashboard
                            </router-link>
                        </li>
                        <li @click.stop>
                            <button
                                :class="['vmc-lists w-100 d-flex align-items-center px-2 py-3 border-0 text-decoration-none bg-transparent', { 'active-link': isParentActive(homeLinks) }]"
                                @click.stop="toggle('home')">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
                                    <path
                                        d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                </svg>
                                <span class="mr-auto">Home</span>
                                <i class="material-symbols-outlined"
                                    :class="{ rotate: open.home }">keyboard_arrow_down</i>
                            </button>
                            <ul class="vmc-dropdown-menu" :class="{ 'dropdown-open': open.home }">
                                <li class="list-unstyled">
                                    <router-link to="/admin/key-visuals"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/key-visuals') }]">
                                        Key Visuals
                                    </router-link>
                                </li>
                                <li class="list-unstyled">
                                    <router-link to="/admin/job-hiring"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/job-hiring') }]">
                                        Job Hiring
                                    </router-link>
                                </li>
                                <li class="list-unstyled">
                                    <router-link to="/admin/crew-and-fleet"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/crew-and-fleet') }]">
                                        Crew and Fleet Status
                                    </router-link>
                                </li>
                                <li class="list-unstyled">
                                    <router-link to="/admin/membership-affiliations"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/membership-affiliations') }]">
                                        Membership and Affiliations
                                    </router-link>
                                </li>
                            </ul>
                        </li>
                        <li @click.stop>
                            <button
                                :class="['vmc-lists w-100 d-flex align-items-center px-2 py-3 border-0 text-decoration-none bg-transparent', { 'active-link': isParentActive(aboutUsLinks) }]"
                                @click.stop="toggle('about')">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 16v-4" />
                                    <path d="M12 8h.01" />
                                </svg>
                                <span class="mr-auto">About Us</span>
                                <i class="material-symbols-outlined"
                                    :class="{ rotate: open.about }">keyboard_arrow_down</i>
                            </button>
                            <ul class="vmc-dropdown-menu" :class="{ 'dropdown-open': open.about }">
                                <li class="list-unstyled">
                                    <router-link to="/admin/the-company"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/the-company') }]">
                                        The Company
                                    </router-link>
                                </li>
                                <li class="list-unstyled">
                                    <router-link to="/admin/our-principals"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/our-principals') }]">
                                        Our Principals
                                    </router-link>
                                </li>
                            </ul>
                        </li>

                        <!-- NEWS -->
                        <li @click.stop>
                            <button
                                :class="['vmc-lists w-100 d-flex align-items-center px-2 py-3 border-0 text-decoration-none bg-transparent', { 'active-link': isParentActive(newsLinks) }]"
                                @click.stop="toggle('news')">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M15 18h-5" />
                                    <path d="M18 14h-8" />
                                    <path
                                        d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-4 0v-9a2 2 0 0 1 2-2h2" />
                                    <rect width="8" height="4" x="10" y="6" rx="1" />
                                </svg>
                                <span class="mr-auto">News</span>
                                <i class="material-symbols-outlined"
                                    :class="{ rotate: open.news }">keyboard_arrow_down</i>
                            </button>
                            <ul class="vmc-dropdown-menu" :class="{ 'dropdown-open': open.news }">
                                <li class="list-unstyled">
                                    <router-link to="/admin/news"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/news') }]">
                                        News Article
                                    </router-link>
                                </li>
                                <li class="list-unstyled">
                                    <router-link to="/admin/news-history"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/news-history') }]">
                                        News History
                                    </router-link>
                                </li>
                            </ul>
                        </li>
                        <li @click.stop>
                            <button
                                :class="['vmc-lists w-100 d-flex align-items-center px-2 py-3 border-0 text-decoration-none bg-transparent', { 'active-link': isParentActive(companyLinks) }]"
                                @click.stop="toggle('company')">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M10 12h4" />
                                    <path d="M10 8h4" />
                                    <path d="M14 21v-3a2 2 0 0 0-4 0v3" />
                                    <path
                                        d="M6 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2" />
                                    <path d="M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16" />
                                </svg>
                                <span class="mr-auto">Company</span>
                                <i class="material-symbols-outlined"
                                    :class="{ rotate: open.company }">keyboard_arrow_down</i>
                            </button>
                            <ul class="vmc-dropdown-menu" :class="{ 'dropdown-open': open.company }">
                                <li class="list-unstyled">
                                    <router-link to="/admin/csr-activities"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/csr-activities') }]">
                                        CSR Activities
                                    </router-link>
                                </li>
                                <li class="list-unstyled">
                                    <router-link to="/admin/office-programs"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/office-programs') }]">
                                        Office Programs
                                    </router-link>
                                </li>
                                <li class="list-unstyled">
                                    <router-link to="/admin/allottees"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/allottees') }]">
                                        Allottees
                                    </router-link>
                                </li>
                            </ul>
                        </li>
                        <li @click.stop>
                            <button
                                :class="['vmc-lists w-100 d-flex align-items-center px-2 py-3 border-0 text-decoration-none bg-transparent', { 'active-link': isParentActive(crewLinks) }]"
                                @click.stop="toggle('crew')">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                    <path d="M16 3.128a4 4 0 0 1 0 7.744" />
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                    <circle cx="9" cy="7" r="4" />
                                </svg>
                                <span class="mr-auto">Crew</span>
                                <i class="material-symbols-outlined"
                                    :class="{ rotate: open.crew }">keyboard_arrow_down</i>
                            </button>
                            <ul class="vmc-dropdown-menu" :class="{ 'dropdown-open': open.crew }">
                                <li class="list-unstyled">
                                    <router-link to="/admin/scholarship-cadetship"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/scholarship-cadetship') }]">
                                        Scholarship and Cadetship
                                    </router-link>
                                </li>
                                <li class="list-unstyled">
                                    <router-link to="/admin/crew-development"
                                        :class="['w-100 d-flex align-items-center text-decoration-none', { 'active-link': isActive('/admin/crew-development') }]">
                                        Crew Development
                                    </router-link>
                                </li>
                            </ul>
                        </li>
                        <li @click.stop>
                            <router-link to="/admin/schedules"
                                :class="['vmc-lists w-100 d-flex align-items-center px-2 py-3 border-0 text-decoration-none', { 'active-link': isActive('/admin/schedules') }]">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M16 14v2.2l1.6 1" />
                                    <path d="M16 4h2a2 2 0 0 1 2 2v.832" />
                                    <path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h2" />
                                    <circle cx="16" cy="16" r="6" />
                                    <rect x="8" y="2" width="8" height="4" rx="1" />
                                </svg>
                                Schedules
                            </router-link>
                        </li>
                        <li @click.stop>
                            <router-link to="/admin/careers"
                                :class="['vmc-lists w-100 d-flex align-items-center px-2 py-3 border-0 text-decoration-none', { 'active-link': isActive('/admin/careers') }]">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M12 12h.01" />
                                    <path d="M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                                    <path d="M22 13a18.15 18.15 0 0 1-20 0" />
                                    <rect width="20" height="14" x="2" y="6" rx="2" />
                                </svg>
                                Career
                            </router-link>
                        </li>
                        <li @click.stop>
                            <router-link to="/admin/contact-us"
                                :class="['vmc-lists w-100 d-flex align-items-center px-2 py-3 border-0 text-decoration-none', { 'active-link': isActive('/admin/contact-us') }]">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path
                                        d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
                                </svg>
                                Contacts
                            </router-link>
                        </li>
                        <li @click.stop>
                            <router-link to="/admin/users-lists"
                                :class="['vmc-lists w-100 d-flex align-items-center px-2 py-3 border-0 text-decoration-none', { 'active-link': isActive('/admin/users-lists') }]">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-user-round-plus-icon lucide-user-round-plus">
                                    <path d="M2 21a8 8 0 0 1 13.292-6" />
                                    <circle cx="10" cy="8" r="5" />
                                    <path d="M19 16v6" />
                                    <path d="M22 19h-6" />
                                </svg>
                                Users Lists
                            </router-link>
                        </li>
                        <li>
                            <button @click="openModal('systembackup')"
                                class="vmc-lists w-100 d-flex align-items-center px-2 py-3 border-0 text-decoration-none bg-transparent">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-archive-restore-icon lucide-archive-restore">
                                    <rect width="20" height="5" x="2" y="3" rx="1" />
                                    <path d="M4 8v11a2 2 0 0 0 2 2h2" />
                                    <path d="M20 8v11a2 2 0 0 1-2 2h-2" />
                                    <path d="m9 15 3-3 3 3" />
                                    <path d="M12 12v9" />
                                </svg>
                                <span class="mr-auto">System Backup</span>
                            </button>
                        </li>
                    </ul>
                </div>
                <footer class="vmc-footer d-flex align-items-center px-3 py-3" @click.stop>
                    <img :src="profile" alt="" class="rounded-circle mr-2"
                        style="width: 45px; height: 45px; object-fit: cover;">
                    <div>
                        <div class="account-name text-nowrap">{{ accountName }}</div>
                        <div class="role-type text-nowrap">{{ userRole }}</div>
                    </div>
                </footer>
            </nav>
        </transition>
    </div>
</template>

<script>
import ToastCMS from './ToastCMS.vue';
export default {
    name: "VMCMobileHeader",
    components: { ToastCMS },
    data() {
        return {
            profile: require("@/assets/profile-white.png"),
            profile2: require("@/assets/profile-black.png"),
            vmcLogo: require("@/assets/veritas_logo.png"),
            showSystemBackupModal: false,
            isOpen: false,
            open: {
                home: false,
                about: false,
                news: false,
                company: false,
                crew: false,
                settings: false
            },
            homeLinks: [
                { path: '/admin/key-visuals' },
                { path: '/admin/job-hiring' },
                { path: '/admin/crew-and-fleet' },
                { path: '/admin/membership-affiliations' }
            ],
            aboutUsLinks: [
                { path: '/admin/the-company' },
                { path: '/admin/our-principals' },
            ],
            newsLinks: [
                { path: '/admin/news' },
                { path: '/admin/news-history' }
            ],
            crewLinks: [
                { path: '/admin/scholarship-cadetship' },
                { path: '/admin/crew-development' }
            ],
            companyLinks: [
                { path: '/admin/csr-activities' },
                { path: '/admin/office-programs' },
                { path: '/admin/allottees' }
            ],
            password: '',
            oldpassword: '',
            newpassword: '',
            confirmnewpassword: '',
            showPassword: false,
            showOldPassword: false,
            showNewPassword: false,
            showConfirmNewPassword: false,
            inputType: ["password", "newpassword", "confirmnewpassword", "oldpassword"],
            showChangePasswordModal: false,
            showEditProfileModal: false,
            isDropdownOpen: false,
            isNavOpen: false,
            isSettingsOpen: false,
            editProfileName: '',
            accountName: localStorage.getItem('vmc_user') || 'Account Name',
            userRole: localStorage.getItem('vmc_role') || 'Admin',
            isAdmin: localStorage.getItem('vmc_role') === 'Admin',
            changePasswordError: '',
            changePasswordLoading: false,
            editProfileError: '',
            editProfileLoading: false,
            tempProfileImage: null,
        }
    },
    methods: {
        async exportData() {
            try {
                const response = await fetch(`${process.env.VUE_APP_API_URL}/api/content?_nocache=${new Date().getTime()}`);
                const data = await response.json();

                // Create a blob and download it
                const fileName = `VMC_Backup_${new Date().toISOString().slice(0, 10)}.json`;
                const json = JSON.stringify(data, null, 2);
                const blob = new Blob([json], { type: 'application/json' });
                const href = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = href;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);

                this.$refs.toast.showToast("Backup downloaded successfully.", "success");
            } catch (error) {
                this.$refs.toast.showToast("Failed to export data.", "error");
            }
        },
        handleImport(event) {
            const file = event.target.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const importedData = JSON.parse(e.target.result);

                    // Basic validation to ensure it's a VMC backup
                    if (!importedData.newsArticles) {
                        throw new Error("Invalid backup format.");
                    }

                    const token = localStorage.getItem('vmc_token');
                    const response = await fetch(`${process.env.VUE_APP_API_URL}/api/update`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(importedData)
                    });

                    if (response.ok) {
                        this.$refs.toast.showToast("Data restored. Reloading...", "success");
                        setTimeout(() => window.location.reload(), 2000);
                    } else {
                        this.$refs.toast.showToast("Server rejected the restore.", "error");
                    }
                } catch (err) {
                    this.$refs.toast.showToast("Error: " + err.message, "error");
                }
            };
            reader.readAsText(file);
            event.target.value = '';
        },
        loadProfile() {
            const savedImage = localStorage.getItem(`vmc_profile_${this.accountName}`);
            if (savedImage) {
                this.profile = savedImage;
                this.profile2 = savedImage;
                const preview = document.getElementById('profilePreview');
                if (preview) preview.src = savedImage;
            } else {
                this.profile = require("@/assets/profile-white.png");
                this.profile2 = require("@/assets/profile-black.png");
            }
        },
        toggleSidebar(e) {
            if (e) e.stopPropagation();
            // Force close profile dropdown
            if (!this.isOpen) {
                this.isDropdownOpen = false;
            }
            this.isOpen = !this.isOpen;
        },
        enterSidebar(el, done) {
            el.style.transform = 'translateX(-100%)';
            el.offsetHeight;
            requestAnimationFrame(() => {
                el.style.transition = 'transform 0.3s ease';
                el.style.transform = 'translateX(0)';
            });
            el.addEventListener('transitionend', done);
        },
        leaveSidebar(el, done) {
            el.style.transform = 'translateX(0)';
            el.offsetHeight;
            requestAnimationFrame(() => {
                el.style.transition = 'transform 0.3s ease';
                el.style.transform = 'translateX(-100%)';
            });
            el.addEventListener('transitionend', done, { once: true });
        },
        handleClickOutside(e) {
            const sidebar = this.$refs.sidebar;
            const toggleBtn = this.$refs.toggleBtn;
            const profileWrap = this.$el.querySelector('.vmc-profile-wrap');

            if (this.isOpen && sidebar && !sidebar.contains(e.target) &&
                toggleBtn && !toggleBtn.contains(e.target)) {
                this.isOpen = false;
            }

            if (this.isDropdownOpen && profileWrap && !profileWrap.contains(e.target)) {
                this.isDropdownOpen = false;
            }
        },
        toggle(menu) {
            Object.keys(this.open).forEach(key => {
                this.open[key] = key === menu ? !this.open[key] : false;
            });
        },
        isActive(path) {
            return this.$route.path === path;
        },
        isParentActive(links) {
            return links.some(link => this.isActive(link.path));
        },
        openActiveDropdown() {
            const menuMap = {
                home: this.homeLinks,
                about: this.aboutUsLinks,
                news: this.newsLinks,
                company: this.companyLinks,
                crew: this.crewLinks,
            };
            Object.keys(menuMap).forEach(key => {
                this.open[key] = menuMap[key].some(link => link.path === this.$route.path);
            });
        },
        resetForm() {
            this.password = '';
            this.oldpassword = '';
            this.newpassword = '';
            this.confirmnewpassword = '';
            this.showOldPassword = false;
            this.showNewPassword = false;
            this.showConfirmNewPassword = false;
            this.editProfileError = '';
            this.changePasswordError = '';
            this.tempProfileImage = null;
        },
        togglePassword() {
            this.showPassword = !this.showPassword;
        },
        toggleOldPassword() {
            this.showOldPassword = !this.showOldPassword;
        },
        toggleNewPassword() {
            this.showNewPassword = !this.showNewPassword;
        },
        toggleConfirmNewPassword() {
            this.showConfirmNewPassword = !this.showConfirmNewPassword;
        },
        toggleDropdown() {
            // Force close sidebar if we are opening profile menu
            if (!this.isDropdownOpen) {
                this.isOpen = false;
            }
            this.isDropdownOpen = !this.isDropdownOpen;
            this.isSettingsOpen = false;
        },
        toggleSettings() {
            this.isSettingsOpen = !this.isSettingsOpen;
        },
        openModal(type) {
            if (type === 'systembackup') this.showSystemBackupModal = true; this.isOpen = false;
        },
        openSettingsModal(type) {
            this.resetForm();
            this.loadProfile();
            if (type === 'password') this.showChangePasswordModal = true;
            if (type === 'profile') {
                this.showEditProfileModal = true;
                this.editProfileName = this.accountName;
            }
            this.isDropdownOpen = false;
            this.isSettingsOpen = false;
        },

        async changePassword() {
            if (!this.oldpassword || !this.newpassword || !this.confirmnewpassword) {
                this.changePasswordError = "Please fill in all fields.";
                return;
            }
            if (this.newpassword !== this.confirmnewpassword) {
                this.changePasswordError = "New passwords do not match.";
                return;
            }
            if (this.oldpassword === this.newpassword) {
                this.changePasswordError = "New password must be different from old password.";
                return;
            }

            this.changePasswordLoading = true;
            this.changePasswordError = '';

            try {
                const token = localStorage.getItem('vmc_token');
                const response = await fetch(`${process.env.VUE_APP_API_URL}/api/change-password`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        oldPassword: this.oldpassword,
                        newPassword: this.newpassword,
                    })
                });

                const data = await response.json();

                if (!response.ok) {
                    this.changePasswordError = data.message || "Failed to change password.";
                    return;
                }
                this.closeSettingsModal();
            } catch (error) {
                this.changePasswordError = "Could not connect to server.";
            } finally {
                this.changePasswordLoading = false;
            }
        },

        async saveProfile() {
            if (!this.password) {
                this.editProfileError = "Password is required to save changes.";
                return;
            }

            this.editProfileLoading = true;
            this.editProfileError = '';

            try {
                const token = localStorage.getItem('vmc_token');
                const response = await fetch(`${process.env.VUE_APP_API_URL}/api/edit-profile`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        newUsername: this.editProfileName,
                        password: this.password
                    })
                });

                const data = await response.json();

                if (!response.ok) {
                    this.editProfileError = data.message || "Incorrect password or server error.";
                    return;
                }

                const oldUsername = this.accountName;
                const newUsername = data.newUsername;

                localStorage.setItem('vmc_token', data.token);
                localStorage.setItem('vmc_user', newUsername);
                this.accountName = newUsername;

                if (this.tempProfileImage === 'REMOVE') {
                    localStorage.removeItem(`vmc_profile_${oldUsername}`);
                    localStorage.removeItem(`vmc_profile_${newUsername}`);
                } else if (this.tempProfileImage) {
                    localStorage.setItem(`vmc_profile_${newUsername}`, this.tempProfileImage);
                    if (oldUsername !== newUsername) localStorage.removeItem(`vmc_profile_${oldUsername}`);
                } else if (oldUsername !== newUsername) {
                    const existingImage = localStorage.getItem(`vmc_profile_${oldUsername}`);
                    if (existingImage) {
                        localStorage.setItem(`vmc_profile_${newUsername}`, existingImage);
                        localStorage.removeItem(`vmc_profile_${oldUsername}`);
                    }
                }

                this.loadProfile();
                window.dispatchEvent(new Event('profile-updated'));
                this.closeSettingsModal();

            } catch (error) {
                this.editProfileError = "Could not connect to server.";
            } finally {
                this.editProfileLoading = false;
            }
        },

        async previewImage(event) {
    const file = event.target.files[0];
    if (!file) return;

    document.getElementById('profilePreview').src = URL.createObjectURL(file);

    const formData = new FormData();
    formData.append('image', file);

    try {
        const response = await fetch('/vmc/api/upload', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('vmc_token')}`
            },
            body: formData
        });

        const data = await response.json();

        if (response.ok) {
            this.tempProfileImage = data.url;
        } else {
            console.error("Upload failed:", data.message);
        }
    } catch (error) {
        console.error("Upload error:", error);
    }
},

        removeProfileImage() {
            const profileImage = document.getElementById('profileImage');
            if (profileImage) profileImage.value = "";
            const defaultBlack = require("@/assets/profile-black.png");

            document.getElementById('profilePreview').src = defaultBlack;
            this.tempProfileImage = 'REMOVE';
        },

        closeSettingsModal() {
            this.showChangePasswordModal = false;
            this.showEditProfileModal = false;
            this.resetForm();
            this.loadProfile();
        },

        closeModal() {
            this.showSystemBackupModal = false;
        },
        logout() {
            localStorage.removeItem('vmc_token');
            localStorage.removeItem('vmc_user');
            this.$router.push('/auth/login');
        },

        onKeyboard(e) {
            if (this.showChangePasswordModal || this.showEditProfileModal) {
                if (e.key === 'Escape') this.closeSettingsModal();
            }

            if (this.showSystemBackupModal) {
                if (e.key === 'Escape') this.closeSettingsModal();
            }
        },
        updateGlobalListener() {
            if (this.isOpen || this.isDropdownOpen) {
                document.addEventListener('click', this.handleClickOutside);
            } else {
                document.removeEventListener('click', this.handleClickOutside);
            }
        }
    },
    watch: {
        $route() {
            this.openActiveDropdown();
            this.isOpen = false;
            this.isDropdownOpen = false;
        },
        isOpen() {
            this.updateGlobalListener();
        },
        isDropdownOpen() {
            this.updateGlobalListener();
        }
    },
    mounted() {
        this.openActiveDropdown();
        window.addEventListener('keydown', this.onKeyboard);
        window.addEventListener('profile-updated', this.loadProfile);
        this.loadProfile();
        this.updateGlobalListener();
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.onKeyboard);
        window.removeEventListener('profile-updated', this.loadProfile);
        document.removeEventListener('click', this.handleClickOutside);
    },

};
</script>

<style scoped>
.toast-container {
    position: fixed;
    width: 300px;
    top: 20px;
    right: 20px;
    z-index: 9999;
}

.vmc-dropdown-menu {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease, opacity 0.3s ease;
    opacity: 0;
}

.vmc-dropdown-menu.dropdown-open {
    max-height: 300px;
    opacity: 1;
}

/* PROFILE DROPDOWN TRANSITION */
.dropdown-enter-active,
.dropdown-leave-active {
    transition: transform 0.25s ease, opacity 0.2s ease;
    transform-origin: top;
}

.dropdown-enter,
.dropdown-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

button {
    transition: 0.3s;
}

.sidebar-close-btn {
    justify-content: end;
    position: fixed;
    top: 10px;
    right: 10px;
}

@media (min-width: 330px) {
    .margin {
        margin-bottom: 60px;
    }

    .vmc-mobile-header {
        height: 60px;
        margin-bottom: 70px;
        background-color: var(--vmc-primary-5);
        z-index: 999;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    }

    .vmc-logo {
        border-bottom: 1px solid #666;
    }

    .vmc-profile-picture {
        height: 30px;
        width: 30px;
        object-fit: cover;
    }

    .toggle-btn {
        font-size: 24px;
    }

    .sidebar {
        width: 310px;
        background-color: var(--vmc-primary-5);
        height: 100dvh;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .vmc-sidebar-lists {
        overflow-y: auto;
        overscroll-behavior: contain;
        flex: 1;
    }

    .vmc-sidebar-lists .vmc-lists,
    .vmc-sidebar-lists .vmc-lists i,
    .vmc-sidebar-lists .vmc-lists span,
    .vmc-sidebar-lists .vmc-lists svg {
        gap: 12px;
        cursor: pointer;
        color: #fff;
        transition: 0.3s;
    }

    .vmc-sidebar-lists .vmc-lists:hover,
    .vmc-sidebar-lists .vmc-lists:hover i,
    .vmc-sidebar-lists .vmc-lists:hover span,
    .vmc-sidebar-lists .vmc-lists:hover svg {
        color: var(--vmc-primary-3);
        stroke: var(--vmc-primary-3);
    }

    .rotate {
        transform: rotate(180deg);
        transition: transform 0.2s ease;
    }

    .vmc-dropdown-menu li {
        margin-left: 12px;
        padding: 6px 0px;
    }

    .vmc-dropdown-menu li,
    .vmc-dropdown-menu a {
        color: #fff;
        transition: 0.3s;
    }

    .vmc-dropdown-menu li:hover,
    .vmc-dropdown-menu a:hover {
        color: var(--vmc-primary-3);
    }

    footer {
        border-top: 1px solid #666;
        padding: 15px 20px;
        overflow: hidden;
        overscroll-behavior: contain;
        flex-shrink: 0;
    }

    footer i,
    footer div,
    span {
        color: #fff;
    }

    .active-link,
    .active-link i,
    .active-link span,
    .active-link svg {
        color: var(--vmc-primary-3) !important;
        stroke: var(--vmc-primary-3) !important;
    }

    .modal-overlay {
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 9999;
        overflow-y: auto;
        overscroll-behavior: contain;
    }

    .modal-content {
        border-radius: 8px;
        width: 400px;
        max-width: 90%;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
        animation: slideDown 0.3s ease-out;
    }

    .modal-header {
        background-color: #f5f5f5;
        border-bottom: 1px solid #ddd;
    }

    .modal-header h2 {
        font-size: 1.2rem;
    }

    .modal-body {
        padding: 20px;
    }

    .modal-actions {
        gap: 10px;
    }

    .vmc-input-box {
        border-bottom: 1px solid #000;
    }

    .vmc-input-box label {
        top: 50%;
        left: 2px;
        transform: translateY(-50%);
        font-size: 1em;
        pointer-events: none;
        transition: 0.3s;
    }

    .vmc-input-box input:focus~label,
    .vmc-input-box input:valid~label {
        top: -5px;
        font-size: 12px;
    }

    .vmc-input-box input {
        outline: none;
        height: 50px;
        font-size: 14px;
    }

    .vmc-input-box .icon {
        right: 8px;
        font-size: 1.2em;
        line-height: 50px;
    }

    .open-settings-btn {
        transition: 0.3s;
    }

    .dropdown-menu-items {
        transition: 0.3s;
    }

    .dropdown-menu-items:hover {
        background-color: #e1e1e1;
    }

    .dropdown-menu-items span {
        transition: transform 0.3s;
    }

    .dropdown-menu-items:hover,
    .open-settings-btn:hover {
        background-color: #f5f5f5 !important;
        cursor: pointer;
    }

    .rotate-settings {
        transform: rotate(90deg);
    }

    .role-type {
        font-size: 15px;
    }

    @keyframes slideDown {
        from {
            transform: translateY(-20px);
            opacity: 0;
        }

        to {
            transform: translateY(0);
            opacity: 1;
        }
    }

    .select-photo {
        position: absolute;
        bottom: 0;
        right: 0;
    }
}

@media (min-width: 1200px) {
    .cms-container {
        display: none;
    }

    .margin {
        margin: 0;
    }

    .vmc-mobile-header {
        display: none;
    }
}
</style>